import { Wrapper } from "../styles/SubscriptionPageStyle";
import { useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { isNativeUser } from "../../utils/common";
import AlertDialog from "../../components/AlertDialog";

const SubscriptionPlans = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isNative = isNativeUser();
    const initialSubscriptionType = queryParams.get("type") || "Premium"; // Default to PRO
    const OS = queryParams.get("OS");

    const [selectedPlan, setSelectedPlan] = useState(initialSubscriptionType);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [pendingPlan, setPendingPlan] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showUnsubscribe, setShowUnsubscribe] = useState(false);
    const [unsubscribeReason, setUnsubscribeReason] = useState("");

    useEffect(() => {
        setSelectedPlan(initialSubscriptionType); // Set default highlight on load
    }, [initialSubscriptionType]);

    const runNativePurchase = (plan) => {
        if (OS === "iOS") {
        try {
            window.webkit?.messageHandlers?.ReadRivalsJS?.postMessage({
                action: "buySubscription",
                value: plan,
            });
            } catch (error) {
                console.log(`Error sending ios javascript callback: ${error}`);
            }
        } else if (OS === "Android") {
            if (window.ReadRivalsJS !== undefined) {
                window.ReadRivalsJS?.buySubscription(plan);
            }
        } else {
            // Web fallback (e.g., redirect to payment page) - left as TODO
        }
    };

    const openDisclaimer = (plan) => {
        setPendingPlan(plan);
        setAcceptedTerms(false); // reset checkbox each time dialog opens
        setShowDisclaimer(true);
    };

    const acceptDisclaimerAndProceed = () => {
        if (!acceptedTerms) return; // safety guard
        if (pendingPlan) {
            setSelectedPlan(pendingPlan);
            runNativePurchase(pendingPlan);
        }
        setPendingPlan(null);
        setShowDisclaimer(false);
    };

    const declineDisclaimer = () => {
        setPendingPlan(null);
        setShowDisclaimer(false);
    };
    // Determine which routes to use for legal documents: native apps should open the public (open) versions
    const isNativeOS = OS === 'iOS' || OS === 'Android';
    const privacyHref = isNativeOS ? '/privacypolicy' : '/privacy-policy';
    const termsHref = isNativeOS ? '/termscondition' : '/terms';

    const openUnsubscribeDialog = (e) => {
        e.preventDefault();
        setUnsubscribeReason("");
        setShowUnsubscribe(true);
    };

    const closeUnsubscribeDialog = () => setShowUnsubscribe(false);

    const runNativeUnsubscribe = (reason) => {
        if (OS === "iOS") {
        try {
            window.webkit?.messageHandlers?.ReadRivalsJS?.postMessage({
                action: "unsubscribe",
                value: reason,
            });
        } catch (error) {
            console.log(`Error sending ios unsubscribe callback: ${error}`);
        }
    } else if (OS === 'Android') {
            try {
                if (window.ReadRivalsJS?.unsubscribe) window.ReadRivalsJS.unsubscribe(reason);
                else if (window.ReadRivalsJS?.unsubscribeSubscription) window.ReadRivalsJS.unsubscribeSubscription(reason);
            } catch (err) { console.log('Error sending Android unsubscribe callback', err); }
        } else {
            console.log('Web unsubscribe (no native bridge):', reason);
        }
    };

    const confirmUnsubscribe = () => {
        runNativeUnsubscribe(unsubscribeReason.trim());
        setShowUnsubscribe(false);
    };
    return (
        <Wrapper>
            <h2>Manage Your Subscription</h2>
            <p className="sub-header-text">
                Choose the plan that best supports your reading journey. Upgrade for advanced speech recognition, AI topic generation and more. You can cancel anytime through your platform account settings.
            </p>
            <div className="plans-container">
                {/* Basic Plan */}
                <div  className={`plan basic ${selectedPlan === "Basic" ? "highlight disabled-card" : ""}`}
                >
                    <h3>BASIC</h3>
                    <p className="price">$4.99 <span>/Monthly</span></p>
                    <ul>
                        <li className="basic-check"><span className="check-icon">✔</span> Access to Free Books</li>
                        <li className="basic-check"><span className="check-icon">✔</span> Basic Speech Recognition</li>
                        <li className="basic-check"><span className="check-icon">✔</span> Basic Reading Tools</li>
                    </ul>
                    <button onClick={() => openDisclaimer("Basic")}>Subscribe</button>
                </div>

                {/* Pro Plan */}
                <div className={`plan pro ${selectedPlan === "Premium" ? "highligt-pro disabled-card" : ""}`}
                >
                    <h3>PRO</h3>
                    <p className="price">$9.99 <span>/Monthly</span></p>
                    <ul>
                        <li className="pro-check"><span className="check-icon">✔</span> Advanced Speech Recognition</li>
                        <li className="pro-check"><span className="check-icon">✔</span> AI Speech & Topic Generation</li>
                        <li className="pro-check"><span className="check-icon">✔</span> Advanced Reading Tools</li>
                        <li className="pro-check"><span className="check-icon">✔</span> Access to Free Books</li>
                    </ul>
                    <button className="pro-btn" onClick={() => openDisclaimer("Premium")} >Upgrade To Premium</button>
                </div>
            </div>
            <AlertDialog
                open={showDisclaimer}
                onClose={declineDisclaimer}
                title="Subscription Terms & Disclaimer"
                description={
                    <div style={{maxWidth:'520px'}}>
                        <p style={{marginTop:0}}>
                            Please review the following before completing your purchase. Your subscription will start immediately after confirmation.
                        </p>
                        <ul style={{paddingLeft:'1.2rem'}}>
                            <li>You authorize ReadRivals to charge the subscription fee to your account every billing period until you cancel.</li>
                            <li>Automatic renewal: Cancels must occur at least 24 hours before the end of the current period to avoid renewal.</li>
                            <li>No partial or pro‑rated refunds for unused time within a billing period.</li>
                            <li>Prices may change; you will be notified in advance and can cancel before the new price applies.</li>
                            <li>Features and availability may vary by device/region.</li>
                            <li>Use is governed by our <a href={privacyHref} target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href={termsHref} target="_blank" rel="noopener noreferrer">Terms & Conditions</a>.</li>
                        </ul>
                        <label style={{display:'flex',alignItems:'flex-start',gap:'0.5rem',marginTop:'0.75rem',cursor:'pointer'}}>
                            <input
                                type="checkbox"
                                checked={acceptedTerms}
                                onChange={(e)=>setAcceptedTerms(e.target.checked)}
                                style={{marginTop:'4px'}}
                            />
                            <span style={{fontSize:'0.9rem'}}>
                                I have read and agree to the recurring billing terms above and consent to immediate access to digital content.
                            </span>
                        </label>
                    </div>
                }
                agreeText="I Agree"
                disagreeText="Cancel"
                runTaskAndClose={acceptDisclaimerAndProceed}
                agreeDisabled={!acceptedTerms}
            />
            <div style={{marginTop:'2.5rem'}}>
                <a href="#unsubscribe" onClick={openUnsubscribeDialog} style={{fontSize:'0.85rem',color:'#555',textDecoration:'underline'}}>Unsubscribe from current plan</a>
            </div>
            <AlertDialog
                open={showUnsubscribe}
                onClose={closeUnsubscribeDialog}
                title="Confirm Unsubscribe"
                description={
                    <div style={{maxWidth:'520px'}}>
                        <p style={{marginTop:0}}>We're sorry to see you go. Your access will remain until the end of the current billing period. Optional: let us know why you're leaving (this helps us improve).</p>
                        <textarea
                            value={unsubscribeReason}
                            onChange={(e)=>setUnsubscribeReason(e.target.value)}
                            placeholder="Reason for unsubscribing (optional)"
                            rows={4}
                            style={{width:'100%',resize:'vertical',padding:'0.5rem',fontFamily:'inherit',fontSize:'0.85rem',borderRadius:4,border:'1px solid #ccc'}}
                        />
                    </div>
                }
                agreeText="Unsubscribe"
                disagreeText="Cancel"
                runTaskAndClose={confirmUnsubscribe}
            />
        </Wrapper>
    );
};

export default SubscriptionPlans;